import { HELP_SECTIONS } from './helpContent';

/**
 * Dois mapas que ligam a Central de Ajuda ao resto do produto.
 *
 * `HELP_GROUPS` agrupa as seções para o sumário lateral — 26 seções numa
 * lista plana viram uma parede. O agrupamento é de LEITURA, não de papel:
 * o filtro por papel continua sendo o campo `audience` de cada seção.
 *
 * `ROUTE_HELP` mapeia rota → seção, e é o que faz o botão "?" do cabeçalho
 * abrir a Ajuda já no assunto da tela em que a pessoa está. Ajuda que exige
 * procurar não é ajuda: quem travou no estoque não deveria ler sobre agenda.
 *
 * Ao criar uma seção nova em `helpContent.tsx`, registre-a aqui também —
 * o teste `apps/web/e2e/51-ajuda.spec.ts` falha se alguma ficar de fora.
 */

export interface HelpGroup {
  label: string;
  /** ids de `HELP_SECTIONS`, na ordem em que aparecem no sumário. */
  sections: string[];
}

export const HELP_GROUPS: HelpGroup[] = [
  {
    label: 'Começando',
    sections: ['primeiros-passos', 'painel'],
  },
  {
    label: 'Operação diária',
    sections: [
      'agenda',
      'pacientes',
      'prontuario',
      'fichas-avaliacao',
      'planos-pacotes',
      'contratos',
      'estoque',
      'arquivos',
    ],
  },
  {
    label: 'Contato com o paciente',
    sections: [
      'funil-vendas',
      'ia-atendimento',
      'comunicacao',
      'sala-espera',
      'agendamento-online',
      'satisfacao',
    ],
  },
  {
    label: 'Dinheiro e resultados',
    sections: ['financeiro', 'relatorios', 'conciliacao'],
  },
  {
    label: 'Sua conta',
    sections: ['notificacoes-perfil', 'marketplace'],
  },
  {
    label: 'Administração',
    sections: [
      'especialidades',
      'equipe',
      'config-admin',
      'consentimento',
      'assinatura',
      'integracoes',
      'seguranca',
    ],
  },
  {
    label: 'Quando empacar',
    sections: ['suporte'],
  },
];

/**
 * Rota (como aparece em `App.tsx`) → id da seção de ajuda correspondente.
 *
 * A chave é o começo do caminho: `/pacientes/42` casa com `/pacientes`.
 * Rotas sem entrada aqui simplesmente não mostram o botão "?".
 */
export const ROUTE_HELP: Record<string, string> = {
  '/painel': 'painel',
  '/agenda': 'agenda',
  '/atendimentos-ia': 'ia-atendimento',
  '/crm': 'funil-vendas',
  '/pacientes': 'pacientes',
  '/prontuario': 'prontuario',
  '/avaliacao': 'fichas-avaliacao',
  '/servicos': 'primeiros-passos',
  '/estoque': 'estoque',
  '/planos': 'planos-pacotes',
  '/faturas': 'financeiro',
  '/equipe': 'equipe',
  '/arquivos': 'arquivos',
  '/consentimento': 'consentimento',
  '/relatorios': 'relatorios',
  '/conciliacao': 'conciliacao',
  '/contas-a-pagar': 'financeiro',
  '/outras-receitas': 'financeiro',
  '/campanhas': 'comunicacao',
  '/templates': 'comunicacao',
  '/marketplace': 'marketplace',
  '/perfil': 'notificacoes-perfil',
  '/administracao': 'config-admin',
  '/configuracoes': 'config-admin',
  '/integracoes': 'integracoes',
  '/suporte': 'suporte',
};

/** Id da seção de ajuda para um caminho, ou null se não houver. */
export function helpSectionForPath(pathname: string): string | null {
  // Casa o prefixo mais longo: /planos/pacotes antes de /planos.
  const hit = Object.keys(ROUTE_HELP)
    .sort((a, b) => b.length - a.length)
    .find((base) => pathname === base || pathname.startsWith(base + '/'));
  return hit ? ROUTE_HELP[hit] : null;
}

/** Âncora da Ajuda para um caminho — pronta pra usar em <Link to>. */
export function helpLinkForPath(pathname: string): string | null {
  const id = helpSectionForPath(pathname);
  return id ? `/ajuda#ajuda-${id}` : null;
}

/** Título da seção, para o tooltip do botão "?". */
export function helpTitleForSection(id: string): string | undefined {
  return HELP_SECTIONS.find((s) => s.id === id)?.title;
}
